import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { PUBLIC_HOST } from '@/lib/publicUrl';

// Captura del deep link con el que el sistema abre la app nativa. Admite dos
// formatos: el scheme propio ("<VITE_APP_SCHEME>:/ruta", vuelta del login de
// Google y del correo de recuperar contrasena) y los App Links / Universal
// Links https del dominio publico (QR de citas y de catalogo).
//
// Debe vivir dentro de <BrowserRouter> porque necesita useNavigate: navegar en
// vez de asignar window.location.href evita recargar el WebView entero, que en
// Android reentrega el intent de lanzamiento y provoca un bucle de arranques.
//
// El efecto se monta UNA sola vez (deps vacias) a proposito: useNavigate cambia
// de identidad en cada cambio de ruta, y si el efecto se reejecutase volveria a
// leer getLaunchUrl(), que sigue devolviendo la URL de arranque, devolviendo al
// usuario a la pantalla inicial cada vez que navega.

const extraerRuta = (url: string, appScheme: string): string | null => {
  const prefix = `${appScheme}:`;

  if (url.startsWith(prefix)) {
    const path = url.slice(prefix.length).replace(/^\/{2,}/, '/');
    return path.startsWith('/') ? path : null;
  }

  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' || u.host !== PUBLIC_HOST) return null;
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
};

const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const ultimaRuta = useRef('');

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const appScheme = import.meta.env.VITE_APP_SCHEME;
    if (!appScheme) return;

    let cancelado = false;

    const abrir = (url: string) => {
      if (cancelado) return;

      const path = extraerRuta(url, appScheme);
      if (!path || path === '/') return;

      // Android puede entregar la misma URL mas de una vez.
      if (path === ultimaRuta.current) return;
      ultimaRuta.current = path;

      navigateRef.current(path, { replace: true });
    };

    CapacitorApp.getLaunchUrl()
      .then((res) => {
        if (res?.url) abrir(res.url);
      })
      .catch(() => undefined);

    const subPromise = CapacitorApp.addListener('appUrlOpen', ({ url }) =>
      abrir(url)
    );

    return () => {
      cancelado = true;
      subPromise.then((sub) => sub.remove());
    };
  }, []);

  return null;
};

export default DeepLinkHandler;